import { ChangeDetectionStrategy, Component, ViewChild } from "@angular/core";
import { Observable, Subscription, Subject } from "rxjs";
import { Router, ActivatedRoute } from "@angular/router";

import * as swal from "bootstrap-sweetalert";
import * as _ from 'lodash';
import * as moment from "moment";

import { CallService } from "../../../services/call.service";
import { ProfileService } from "app/services/profile.service";
import { Call } from "../../../models/call";
import { PagedListAdapter } from "../../../common/paged-list-adapter";
import { PagedResult } from "../../../models/PagedResult";
import { CallFilterOptions } from "app/dto/callFilterOptions";
import { BsLocaleService } from 'ngx-bootstrap';
import { Ng2selectObject } from "app/dto/ng2selectObject";
import { ProblemTemplateService } from "app/services/problemTemplate.service";

@Component({
  templateUrl: './calls.component.html',
  styleUrls: ['./calls.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class CallsComponent {
  @ViewChild('dropdown')
  private searchDropdown: any;

  constructor(
    private _callService: CallService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _problemTemplatesService: ProblemTemplateService,
    private _profileService: ProfileService,
    private _localeService: BsLocaleService
  ) {
    this._callsAdapter = new CallsComponent.CallsAdapter(this._callService);
    this.callConfirmationPermission = this._profileService.checkHasCallConfirmationPermission();
  }

  ngOnInit(): void {
    this._localeService.use('ru');
    this.loadInitData();
  }

  private setFilterOptions(): void {
    this.setQueryParams(
      {
        number: this._filterOptions.number,
        status: this._statusActiveItem ? this._statusActiveItem.id : null,
        author: this._authorActiveItem ? this._authorActiveItem.id : null,
        problemTemplateIds: (this._problemTemplateActiveItems.length > 0) ? _.map(this._problemTemplateActiveItems, "id").join() : null,
        createdStart: this.formatDate(this._filterOptions.createdRange, 0),
        createdEnd: this.formatDate(this._filterOptions.createdRange, 1),
        isConfirmed: this._isConfirmedActiveItem ? this._isConfirmedActiveItem.id : null
      });
  }

  private resetFilterOptions(): void {

    //Очистка фильтра
    this._filterOptions.number = null;
    this._statusActiveItem = null;
    this._authorActiveItem = null;
    this._problemTemplateActiveItems = [];
    this._filterOptions.createdRange = null;
    this._isConfirmedActiveItem = null;

    this.setFilterOptions();
  }

  private setQueryParams(queryParams: object) {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {
        ...this._activatedRoute.snapshot.queryParams,
        ...queryParams
      }
    });
    this.searchDropdown.hide();
  }

  private loadPageWithUrlFilterParams() {
    this._activatedRoute.queryParams.subscribe(params => {

      if (params['createdStart'] && params['createdEnd'])
        this._filterOptions.createdRange = [new Date(params['createdStart']), new Date(params['createdEnd'])];
      else
        this._filterOptions.createdRange = [];

      if (params['problemTemplateIds']) {
        this._filterOptions.problemTemplateIds = params['problemTemplateIds'].split(',');
        this._problemTemplateActiveItems = _(this._problemTemplateSelectItems).keyBy('id').at(this._filterOptions.problemTemplateIds).value(); // Установка значений в элемент Select
      } else {

        this._problemTemplateActiveItems = [];
        this._filterOptions.problemTemplateIds = null;
      }

      if (params['status']) {
        this._filterOptions.status = params['status'];
        this._statusActiveItem = _.find(this._statusesSelectItems, function (status) { return status.id == params['status']; });
      } else {
        this._filterOptions.status = null;
        this._statusActiveItem = null;
      }

      if (params['author']) {
        this._authorActiveItem = _.find(this._authorSelectItems, function (author) { return author.id == params['author']; });
        this._filterOptions.author = this._authorActiveItem.text;
      } else {
        this._filterOptions.author = null;
        this._authorActiveItem = null;
      }

      if (params['isConfirmed'] && this.callConfirmationPermission) {
        this._isConfirmedActiveItem = _.find(this._isConfirmedSelectItems, function (item) { return item.id == params['isConfirmed']; });
        this._filterOptions.isConfirmed = !!+this._isConfirmedActiveItem.id;
      } else {
        this._filterOptions.isConfirmed = null;
        this._isConfirmedActiveItem = null;
      }

      this._filterOptions.number = params['number'] ? params['number'] : null;

      this._callsAdapter.setFilterOptions(this._filterOptions);

      this.setSearchString();
    });
  }

  private setSearchString() {
    let searchStrings: string[] = new Array();

    if (this._filterOptions) {
      if (this._filterOptions.number)
        searchStrings.push("Номер:(" + this._filterOptions.number + ")");

      if (this._filterOptions.createdRange) {
        if (this._filterOptions.createdRange.length >= 1 && this._filterOptions.createdRange[0])
          searchStrings.push("После:(" + moment(this._filterOptions.createdRange[0]).format("DD.MM.YYYY") + ")");

        if (this._filterOptions.createdRange.length == 2 && this._filterOptions.createdRange[1])
          searchStrings.push("До:(" + moment(this._filterOptions.createdRange[1]).format("DD.MM.YYYY") + ")");
      }

      if (this._filterOptions.status)
        searchStrings.push("Статус:(" + this._statusesRu[this._filterOptions.status] + ")");

      if (this._filterOptions.problemTemplateIds != null && this._filterOptions.problemTemplateIds.length > 0) {
        var problemTemplateSearchValue = _(this._problemTemplateSelectItems).keyBy('id').at(this._filterOptions.problemTemplateIds).map('text').value().join('|');
        searchStrings.push("Тема:(" + problemTemplateSearchValue + ")");
      }

      if (this._filterOptions.author)
        searchStrings.push("Автор:(" + this._filterOptions.author + ")");
      
      if (this._filterOptions.isConfirmed != null && this.callConfirmationPermission)
        searchStrings.push("Подтверждено:(" + this._isConfirmedRu[+this._filterOptions.isConfirmed] + ")");
    }

    this._searchString = _.join(searchStrings, ' ');
  }

  private formatDate(dateRange: Date[], index: number) {
    if (dateRange == null || dateRange[index] == null)
      return null;

    return moment(dateRange[index]).toISOString();
  }

  private removeProblemTemplateItem(problemTemplate: Ng2selectObject): void {
    _.remove(this._problemTemplateActiveItems, function (item) { return item.id == problemTemplate.id; });
  }

  private resetStatusFilter() {
    this.setQueryParams(
      {
        status: null
      });
  }

  private loadInitData() {
    Observable.forkJoin(this._problemTemplatesService.getCallProblemTemplate(), this._callService.getCallAuthors())
      .subscribe(results => {
        const [problemTemplates, authors] = results;
        this._problemTemplateSelectItems = _.map(problemTemplates, function (obj) { return new Ng2selectObject(obj.id.toString(), obj.name) });

        this._authorSelectItems = _.map(authors, function (author) {
          return (author != null) ? new Ng2selectObject("@" + author, author)
            : new Ng2selectObject("!", "Диспетчер");
        });

        this.loadPageWithUrlFilterParams();
      }, err => {
        this._problemTemplateSelectItems = null;
        swal({
          title: 'Ошибка',
          text: 'Не удалось получить данные. Попробуйте снова',
          type: 'error',
          confirmButtonText: 'Попробовать еще раз'
        }, () => {
          this.ngOnInit();
        });
      });
  }

  private callConfirmationPermission: boolean
  private _filterOptions: CallFilterOptions = new CallFilterOptions();
  private _problemTemplatesLoadSubscription: Subscription;
  private _searchString: string;
  private _authorsLoadSubscription: Subscription;
  private _problemTemplateSelectItems: Ng2selectObject[];
  private _authorSelectItems: Ng2selectObject[];

  public _statusActiveItem: Ng2selectObject;
  public _authorActiveItem: Ng2selectObject;
  public _isConfirmedActiveItem: Ng2selectObject;

  private _problemTemplateActiveItems: any[];
  private _callsAdapter: CallsComponent.CallsAdapter;
  private _statusesRu: { [id: number]: string; } = { 0: "Открыто", 1: "Закрыто" };
  private _statusesSelectItems = _.map(this._statusesRu, function (key, value) { return new Ng2selectObject(value, key.toString()) });

  private _isConfirmedRu: { [id: number]: string; } = { 0: "Нет", 1: "Да" };
  private _isConfirmedSelectItems = _.map(this._isConfirmedRu, function (key, value) { return new Ng2selectObject(value, key.toString()) });
}

export namespace CallsComponent {

  export class CallsAdapter implements PagedListAdapter<Call> {

    private _filterOptions: CallFilterOptions;
    private _callSubject: Subject<any> = new Subject();

    constructor(
      private _callService: CallService
    ) {
    }

    setFilterOptions(filterOptions: CallFilterOptions) {
      this._filterOptions = filterOptions;
      this._callSubject.next();
    }

    getItems(count: number, skip: number, sortBy: string, isDesc: boolean): Observable<PagedResult<Call>> {
      return this._callSubject.switchMap(_ => this._callService.getCalls(skip, count, sortBy, isDesc, this._filterOptions));
    }
  }
}



// WEBPACK FOOTER //
// /app/SPA/src/app/client/call/list/calls.component.ts